import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getCustomDimensionForIntent,
  reportEvent,
  ReportEventArgs,
  reportViewEvent,
  ReportViewEventArgs,
} from './tracking'
import { ConfigFromServer, Flow, IntegrationType, TealiumProfileFromServer } from '../@types/config'
import { OptionalLeadSource } from '../@types/lead'
import { ReduxState } from '../store/reducers'
import { WizardFlowType } from '../@types/wizardFlowType'

declare global {
  interface Window {
    IS24?: {
      TEALIUM?: {
        tracking: {
          report: ReportFn
        }
      }
      RLE?: {
        configuration?: ConfigFromServer
        optionalTealiumProfile?: TealiumProfileFromServer
        optionalLeadSource?: OptionalLeadSource
      }
    }
  }
}

const getSvcModuleName = (isExternalIntegration: boolean, wizardFlowType?: WizardFlowType): string => {
  const prefix = isExternalIntegration ? 'coop_' : ''
  const flow = wizardFlowType === WizardFlowType.IBW ? 'ibw_' : ''

  return window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    ? window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    : `${prefix}${flow}realtor-leadengine`
}

const getSvcModuleNameByFlow = (isExternalIntegration: boolean, flow?: Flow): string => {
  const prefix = isExternalIntegration ? 'coop_' : ''
  const flowIndicator = flow === Flow.IBW ? 'ibw_' : ''

  return window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    ? window.IS24?.RLE?.optionalTealiumProfile?.svcModuleName
    : flow === Flow.SOLAR
    ? 'solar-leadengine'
    : `${prefix}${flowIndicator}realtor-leadengine`
}

const reportTrackingEvent = createAsyncThunk<void, ReportEventArgs, { state: ReduxState }>(
  'tracking/reportTrackingEvent',
  async ({ isTrackingIntent = true, ...reportEventArgs }, { getState }) => {
    const state = getState() as ReduxState
    const integrationType = state.config.integrationType
    const cooperationId = state.config.coopId

    if (!isTrackingEnabled(getState)) {
      return
    }

    const {
      lead: { intent, extendedBuyerLead },
      config: { flow },
      user,
      location,
    } = state

    await reportEvent(
      {
        ...reportEventArgs,
        cooperationId,
        ...{
          ...((reportEventArgs.customDimensions || intent) && {
            customDimensions: {
              svc_module_name: getSvcModuleNameByFlow(integrationType === IntegrationType.EXTERNAL, flow),
              ...reportEventArgs.customDimensions,
              ...(intent &&
                isTrackingIntent && { ga_cd_rle_intent: getCustomDimensionForIntent(intent, extendedBuyerLead) }),
            },
          }),
        },
        flow,
      },
      user,
      location.data?.postcode
    )
  }
)

const isTrackingEnabled = (getState: () => unknown): boolean => {
  const state = getState() as ReduxState
  const enableTealium = state.config.tealium
  const isFrontendTrackingEnabled = state.config.enableFrontendTracking
  return enableTealium || isFrontendTrackingEnabled
}

const reportTrackingViewEvent = createAsyncThunk<void, ReportViewEventArgs, { state: ReduxState }>(
  'tracking/reportTrackingViewEvent',
  async (reportViewEventArgs, { getState }) => {
    if (!isTrackingEnabled(getState)) {
      return
    }
    const state = getState()
    const {
      user,
      location,
      address,
      config: { integrationType, flow, coopId },
    } = state

    const cooperationId = coopId

    await reportViewEvent(
      {
        ...reportViewEventArgs,
        cooperationId,
        ...{
          customDimensions: {
            svc_module_name: getSvcModuleNameByFlow(integrationType === IntegrationType.EXTERNAL, flow),
            ...(reportViewEventArgs.customDimensions && {
              ...reportViewEventArgs.customDimensions,
            }),
          },
        },
      },
      user,
      location.data?.postcode,
      address.data?.street,
      flow
    )
  }
)

export { reportTrackingEvent, reportTrackingViewEvent, getSvcModuleName, getSvcModuleNameByFlow }
